import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '@material-ui/core/Modal';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Slider from '@material-ui/core/Slider';
import RotateRightIcon from '@material-ui/icons/RotateRight';
import styled from 'styled-components';
import { Box, Flex } from '@rebass/grid';
import { colors, scrims } from '../../config/theme';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${scrims.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
`;

// && doubles the class specificity so these win over MuiCard-root's
// overflow: hidden, which otherwise clips the content instead of scrolling
const ModalCard = styled(Card)`
  && {
    width: 100%;
    max-width: 720px;
    max-height: 100%;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }
`;

const CropFrame = styled.div`
  width: min(100%, 360px);
  aspect-ratio: 1 / 1;
  overflow: hidden;
  position: relative;
  border: 2px solid ${colors.primary};
  border-radius: 8px;
  background: ${colors.surfaceInverse};
  margin: 0 auto;
`;

const CropImage = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: ${({ x, y, zoom, rotation }) =>
    `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${zoom}) rotate(${rotation}deg)`};
  transform-origin: center center;
  max-width: 100%;
  max-height: 100%;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
`;

const HiddenCanvas = styled.canvas`
  display: none;
`;

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const AvatarCropModal = ({
  open,
  file,
  onClose = () => {},
  onConfirm = () => {},
}) => {
  const canvasRef = useRef(null);
  const [imageSrc, setImageSrc] = useState('');
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    let active = true;

    if (!file || !open) {
      setImageSrc('');
      setZoom(1);
      setRotation(0);
      setOffsetX(0);
      setOffsetY(0);
      return undefined;
    }

    readFileAsDataUrl(file).then((src) => {
      if (!active) return;
      setImageSrc(src);
      setZoom(1);
      setRotation(0);
      setOffsetX(0);
      setOffsetY(0);
    });

    return () => {
      active = false;
    };
  }, [file, open]);

  const safeOffsets = useMemo(
    () => ({
      x: clamp(offsetX, -140, 140),
      y: clamp(offsetY, -140, 140),
    }),
    [offsetX, offsetY],
  );

  const handleRotate = () => {
    setRotation((current) => (current + 90) % 360);
  };

  const handleConfirm = async () => {
    if (!imageSrc || !canvasRef.current || !file) return;

    setIsSaving(true);

    try {
      const image = await loadImage(imageSrc);
      const canvas = canvasRef.current;
      const size = 500;
      const ctx = canvas.getContext('2d');
      canvas.width = size;
      canvas.height = size;
      ctx.clearRect(0, 0, size, size);
      ctx.save();
      ctx.translate(size / 2 + safeOffsets.x, size / 2 + safeOffsets.y);
      ctx.rotate((rotation * Math.PI) / 180);

      const fitScale = Math.max(size / image.width, size / image.height);
      const finalScale = fitScale * zoom;

      ctx.scale(finalScale, finalScale);
      ctx.drawImage(image, -image.width / 2, -image.height / 2);
      ctx.restore();

      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.9),
      );

      const croppedFile = new File([blob], `${file.name.split('.')[0]}-cropped.jpeg`, {
        type: 'image/jpeg',
      });

      onConfirm(croppedFile);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Overlay>
        <ModalCard>
          <CardContent>
            <Flex justifyContent="space-between" alignItems="center" mb="16px">
              <Typography variant="h6">Crop profile image</Typography>
              <Button onClick={handleRotate} startIcon={<RotateRightIcon />}>
                Rotate
              </Button>
            </Flex>

            <Typography variant="body2" color="textSecondary">
              Adjust the crop, then save your image.
            </Typography>

            <Box mt="20px">
              <CropFrame>
                {imageSrc ? (
                  <CropImage
                    src={imageSrc}
                    alt="Crop preview"
                    x={safeOffsets.x}
                    y={safeOffsets.y}
                    zoom={zoom}
                    rotation={rotation}
                  />
                ) : null}
              </CropFrame>
            </Box>

            <Box mt="20px">
              <Typography gutterBottom>Zoom</Typography>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.05}
                onChange={(_, value) => setZoom(value)}
              />
            </Box>

            <Box mt="12px">
              <Typography gutterBottom>Horizontal crop</Typography>
              <Slider
                value={offsetX}
                min={-140}
                max={140}
                step={1}
                onChange={(_, value) => setOffsetX(value)}
              />
            </Box>

            <Box mt="12px">
              <Typography gutterBottom>Vertical crop</Typography>
              <Slider
                value={offsetY}
                min={-140}
                max={140}
                step={1}
                onChange={(_, value) => setOffsetY(value)}
              />
            </Box>

            <Flex justifyContent="flex-end" mt="24px">
              <Box mr="12px">
                <Button onClick={onClose} variant="outlined" disabled={isSaving}>
                  Cancel
                </Button>
              </Box>
              <Button
                onClick={handleConfirm}
                variant="contained"
                color="primary"
                disabled={!imageSrc || isSaving}
              >
                {isSaving ? 'Saving...' : 'Use image'}
              </Button>
            </Flex>

            <HiddenCanvas ref={canvasRef} />
          </CardContent>
        </ModalCard>
      </Overlay>
    </Modal>
  );
};

export default AvatarCropModal;
